import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from "vue-router";
import { shouldSuppressVueRouterSync } from "./reactNativePaths";

/**
 * Vue 메모리 라우터의 `afterEach` 가드 본체.
 *
 * Vue 라우터의 이동이 React Router URL 을 끌고 가는 사이드이펙트를 차단한다.
 * 단위 테스트 가능하도록 별도 모듈로 분리했다 (Vue 컴포넌트 import 의존성 없음).
 *
 * 전파 단위는 `fullPath` 다 — 쿼리가 화면 상태를 실어 나르는 경로(예: 트레이스 탐색기의
 * `?run=…&hand=…`)가 있어서, path 만 넘기면 React Router 커밋이 쿼리 없는 URL 로 덮어써
 * 그 화면이 자기 위치를 잃는다.
 *
 * 가드 정책:
 * 1. React Router 의 현재 path 가 React-native 책임 경로면 (예: `/documents/*`)
 *    Vue 라우터의 이동을 React Router 로 전파하지 않는다. Vue 앱은 Pinia 보존
 *    목적으로만 mount 되어 있고, 사용자에게 보이는 화면은 React 가 책임진다.
 * 2. Vue 라우터의 "/" 복귀는 초기 mount fallback 일 가능성이 높아 전파하지 않는다.
 * 3. 마지막으로 의도한 대상(intendedTarget) 과 같으면 중복 push 이므로 전파하지 않는다.
 * 4. React Router가 이미 같은 대상에 있으면 전파할 변화가 없으므로 생략한다.
 *
 * `intendedTarget` 는 가드 함수 내부 클로저에서만 변경되며 외부로 노출되지 않는다.
 */
export function createAfterEachGuard(deps: {
  /** 쿼리를 포함한 이동 대상(`fullPath`)을 받는다. */
  navigate: (target: string) => void;
  /** React Router 가 소유한 현재 pathname (쿼리 미포함). */
  currentReactPath: () => string;
}) {
  // 첫 호출 시 비교 대상이 없으므로 빈 문자열로 시작. `to.fullPath === ""` 인 경우는 없다.
  let intendedTarget = "";
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalizedLoaded
  ) => {
    if (shouldSuppressVueRouterSync(deps.currentReactPath())) {
      // React 책임 경로가 URL 을 점유 중 — Vue 라우터 이동을 전파하지 않는다.
      // intendedTarget 는 계속 최신 Vue 대상으로 갱신하여 이후 Vue-bridged 페이지 복귀 시
      // 첫 transition 이 오래된 값과 비교돼 누락되는 일이 없게 한다.
      intendedTarget = to.fullPath;
      return;
    }
    // 쿼리까지 실어 보낸다. 쿼리가 없으면 `fullPath === path` 라 기존 동작과 같고,
    // 쿼리가 있으면 pathname 만 돌려주는 currentReactPath() 와 절대 같지 않아
    // 같은 경로에서의 쿼리 변경도 전파된다.
    const target = to.fullPath;
    if (
      to.path !== "/" &&
      target !== intendedTarget &&
      target !== deps.currentReactPath()
    ) {
      deps.navigate(target);
    }
    // 중복 제거: 같은 의도 대상의 반복 전이를 React Router 로 다시 push 하지 않는다.
    intendedTarget = target;
  };
}
